'use client';

/**
 * SysObeyaHighCcModulesTableCard — per-module high-CC breakdown.
 *
 * Slices `response.data[0].modules[]` from the system_obeya_view
 * high_cc_functions composite call. Sortable; identifies which
 * modules are carrying the complexity debt.
 *
 * Typography from `useTypographyBundle('canvas_dashboard')` — same
 * pattern as `module-table-card.tsx`. Container chrome (p-6 title
 * band, cell padding, row hover) lives here as render concerns per
 * the mode7 doctrine. Chrome strings come from `response.labels`.
 *
 * Click-to-drill-down on rows is deferred to sel:cc_module in a
 * later iter.
 *
 * @see klyde_edge/ui_config/composition/cards/system_obeya.py — content_key="sys_obeya_high_cc_modules_table"
 * @see klyde_edge/ui_config/labels/system_obeya.py — high_cc_modules_table_*, high_cc_col_*
 */

import { useState, useMemo } from 'react';
import { Layers, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { type TypographyBundleSlots } from '@/lib/design/typography';
import { useCardTypography } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { useSystemObeyaView, type SystemObeyaLabels } from './use-system-obeya-view';

interface Props {
  view_name?: string;
}

type SortDir = 'asc' | 'desc';
type Align = 'left' | 'right';

interface ColumnDef {
  key: string;
  label_key: string;
  align: Align;
}

const COLUMNS: ColumnDef[] = [
  { key: 'package',             label_key: 'high_cc_col_module',     align: 'left'  },
  { key: 'high_cc_count',       label_key: 'high_cc_col_high_cc',    align: 'right' },
  { key: 'total_functions',     label_key: 'high_cc_col_total_fns',  align: 'right' },
  { key: 'high_cc_percentage',  label_key: 'high_cc_col_high_pct',   align: 'right' },
];

export function SysObeyaHighCcModulesTableCard({ view_name = 'high_cc_functions' }: Props) {
  const { data, isLoading, queryKey } = useSystemObeyaView(view_name);
  const T = useCardTypography();
  const L: SystemObeyaLabels = data?.labels ?? {};

  const [sortKey, setSortKey] = useState<string>('high_cc_count');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo<Record<string, unknown>[]>(() => {
    const first = (data?.data ?? [])[0] as { modules?: Record<string, unknown>[] } | undefined;
    const modules = first?.modules ?? [];
    return [...modules].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? '');
      const bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
  }, [data, sortKey, sortDir]);

  // Card export — markdown table of the sorted module breakdown.
  const title = L.high_cc_modules_table_title ?? '';
  useCardExport(useMemo(() => {
    const headers = COLUMNS.map((c) => L[c.label_key] ?? c.key);
    const md = [
      `| ${headers.join(' | ')} |`,
      `| ${headers.map(() => '---').join(' | ')} |`,
      ...rows.map((row) =>
        `| ${COLUMNS.map((c) => formatRowCellForExport(c, row, L)).join(' | ')} |`,
      ),
    ].join('\n');
    // Scene json mirrors painted cell strings ("4.3%", "188") — the
    // formatter's OUTPUT. Raw underlying numbers travel in the
    // capture envelope's `payloads` section, keeping the
    // `format(payload.X, labels) == scene.row.X` assertion checkable.
    const visibleRows = rows.map((r) =>
      Object.fromEntries(COLUMNS.map((c) => [c.key, formatRowCellForExport(c, r, L)]))
    );
    return {
      label: title,
      text: rows.map((r) =>
        COLUMNS.map((c) => `${L[c.label_key] ?? c.key}=${formatRowCellForExport(c, r, L)}`).join(', ')
      ).join('\n'),
      markdown: md,
      json: { columns: COLUMNS.map((c) => c.key), rows: visibleRows },
      content_key: 'sys_obeya_high_cc_modules_table',
      surface: 'canvas',
      state: { sort_key: sortKey, sort_dir: sortDir },
      // Status = paintable content, not the query flag.
      status: (data ? 'loaded' : 'loading') as 'loading' | 'loaded',
      query_key: queryKey,
    };
  }, [rows, L, title, sortKey, sortDir, data, isLoading, queryKey]));

  if (isLoading || !data) {
    return (
      <CardShell>
        <div className="p-6">
          <div className={T.body}>{L.loading_message ?? ''}</div>
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <div className="p-6 border-b border-border/50">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-primary" />
          <h2 className={T.header}>{L.high_cc_modules_table_title ?? ''}</h2>
        </div>
      </div>
      <div className="overflow-auto flex-1">
        <table className="w-full">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b border-border/50">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => {
                    if (sortKey === col.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    else { setSortKey(col.key); setSortDir('desc'); }
                  }}
                  className={cn(
                    T.metadata,
                    'uppercase tracking-wider font-semibold px-3 py-3 cursor-pointer hover:text-primary select-none',
                    col.align === 'right' ? 'text-right' : 'text-left',
                  )}
                >
                  <span className="inline-flex items-center gap-1">
                    {L[col.label_key] ?? ''}
                    {sortKey === col.key && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={(row.package as string) ?? `row-${idx}`} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                {COLUMNS.map((col) => (
                  <Cell key={col.key} col={col} row={row} T={T} L={L} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardShell>
  );
}

function Cell({
  col,
  row,
  T,
  L,
}: {
  col: ColumnDef;
  row: Record<string, unknown>;
  T: TypographyBundleSlots;
  L: SystemObeyaLabels;
}) {
  const dash = L.dash_placeholder ?? '—';

  if (col.key === 'package') {
    const moduleName = (row.display_name as string) || (row.package as string) || '';
    return (
      <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
        <div className={cn(T.name, 'font-mono')}>{moduleName || dash}</div>
      </td>
    );
  }

  const value = row[col.key];
  const isPercent = col.key.endsWith('_percentage') || col.key.endsWith('_pct');
  const rendered = value == null ? dash : isPercent ? `${fmt(value)}%` : fmt(value);

  return (
    <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
      <span className={cn(T.body, 'font-mono tabular-nums')}>{rendered}</span>
    </td>
  );
}

function CardShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-border bg-card shadow-sm h-full overflow-hidden flex flex-col">{children}</div>;
}
function fmt(n: unknown): string {
  if (n == null || typeof n !== 'number') return '—';
  return n.toLocaleString();
}

function formatRowCellForExport(col: ColumnDef, row: Record<string, unknown>, L: SystemObeyaLabels): string {
  const dash = L.dash_placeholder ?? '—';
  if (col.key === 'package') {
    return (row.display_name as string) || (row.package as string) || dash;
  }
  const value = row[col.key];
  if (value == null) return dash;
  const isPercent = col.key.endsWith('_percentage') || col.key.endsWith('_pct');
  return isPercent ? `${fmt(value)}%` : fmt(value);
}
